import { useMemo, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  Area,
  ComposedChart,
  CartesianGrid,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CompoundSwitcher } from "@/components/CompoundSwitcher";
import { activeAmountAt } from "@/lib/pk/engine";
import { buildDoseModel } from "@/lib/pk/model";
import { sampleProjection } from "@/lib/pk/chart";
import { steadyStateAverage } from "@/lib/pk/recommend";
import { serumModelForPreset } from "@/lib/pk/serum";
import {
  isTestosteronePreset,
  testosteroneBaseFraction,
} from "@/lib/pk/testosterone";
import { useCompounds } from "@/lib/state/compounds";
import { ProjectionTimingPicker } from "@/components/ProjectionTimingPicker";
import { useProjectionTiming } from "@/lib/state/useProjectionTiming";
import { useNow } from "@/lib/state/useNow";
import { formatMg, formatShortDateTime } from "@/lib/format";
import { cn } from "@/lib/utils/cn";

export const Route = createFileRoute("/_tabs/graph")({
  component: GraphScreen,
});
const DAY = 86_400_000;
const WINDOWS = {
  "7d": { back: 3, forward: 4, step: 30 },
  "14d": { back: 7, forward: 7, step: 60 },
  "30d": { back: 14, forward: 16, step: 120 },
  "90d": { back: 30, forward: 60, step: 360 },
};

function GraphScreen() {
  const { active, view } = useCompounds();
  const now = useNow();
  const [timing] = useProjectionTiming();
  const [route, setRoute] = useState<"im" | "sc">("im");
  const [windowKey, setWindowKey] = useState<keyof typeof WINDOWS>("14d");
  const chart = useMemo(() => {
    if (!active || !view) return null;
    try {
      const cfg = WINDOWS[windowKey];
      const from = new Date(+now - cfg.back * DAY);
      const to = new Date(+now + cfg.forward * DAY);
      const model = buildDoseModel({
        now,
        doses: [
          ...view.confirmedDoses,
          ...view.pendingDoses,
          ...view.skippedDoses,
        ],
        schedule: active.schedule,
        compound: active.preset,
        scenario: timing,
        count: Math.ceil((cfg.forward * 24) / active.schedule.cadenceHours) + 1,
      });
      const proposed = model.upcoming
        .filter((d) => d.kind === "take")
        .map((d) => ({ takenAt: d.takeAt, amount: d.doseMg }));
      // When history is unresolved, display confirmed-dose decay only.
      const allDoses = [
        ...model.confirmedDoses,
        ...(model.unresolvedDoses.length || model.calculationError
          ? []
          : model.plannedDoses),
        ...proposed,
      ];
      const baseFraction = testosteroneBaseFraction(active.preset);
      const multiplier = 1;
      const samples = sampleProjection({
        from,
        to,
        now,
        doses: allDoses,
        compound: active.preset,
        schedule: active.schedule,
        stepMinutes: cfg.step,
        multiplier,
      });
      const serumModel = serumModelForPreset(
        active.preset.id,
        active.preset.id === "test-e" ? route : "im",
      );
      const serumSamples = serumModel
        ? sampleProjection({
            from,
            to,
            now,
            doses: allDoses,
            compound: serumModel.compound,
            schedule: active.schedule,
            stepMinutes: cfg.step,
            multiplier: serumModel.multiplier,
          }).map((sample) => ({
            ...sample,
            value: sample.value + serumModel.backgroundNgDl,
            actual:
              sample.actual === null
                ? null
                : sample.actual + serumModel.backgroundNgDl,
            projected:
              sample.projected === null
                ? null
                : sample.projected + serumModel.backgroundNgDl,
            reference: sample.reference + serumModel.backgroundNgDl,
          }))
        : null;
      const serum =
        serumModel && serumSamples
          ? {
              model: serumModel,
              samples: serumSamples,
              current:
                activeAmountAt(now, model.confirmedDoses, serumModel.compound) *
                  serumModel.multiplier +
                serumModel.backgroundNgDl,
              peak: Math.max(...serumSamples.map((sample) => sample.value)),
              mean:
                steadyStateAverage(now, active.schedule, serumModel.compound) *
                  serumModel.multiplier +
                serumModel.backgroundNgDl,
            }
          : null;
      const doseMarkers = [
        ...model.confirmedDoses.map((dose) => ({
          t: +dose.takenAt,
          amount: dose.amount,
          kind: "confirmed",
        })),
        ...(model.unresolvedDoses.length || model.calculationError
          ? []
          : model.plannedDoses.map((dose) => ({
              t: +dose.takenAt,
              amount: dose.amount,
              kind: "saved plan",
            }))),
        ...model.upcoming
          .filter((dose) => dose.kind === "take")
          .map((dose) => ({
            t: +dose.takeAt,
            amount: dose.doseMg,
            kind: "scenario",
          })),
      ].filter((dose) => dose.t >= +from && dose.t <= +to);
      return {
        serum,
        doseMarkers,
        model,
        samples,
        from,
        to,
        baseFraction,
        current:
          activeAmountAt(now, model.confirmedDoses, active.preset) * multiplier,
        peak: Math.max(...samples.map((s) => s.value)),
        mean:
          steadyStateAverage(now, active.schedule, active.preset) * multiplier,
        error: null,
      };
    } catch (error) {
      return {
        error:
          error instanceof Error
            ? error.message
            : "Unable to calculate this projection",
      };
    }
  }, [active, view, now, windowKey, timing, route]);
  if (!active || !view || !chart) return null;

  return (
    <SafeAreaView className="flex-1 bg-bg" edges={["top"]}>
      <View className="flex-row items-center justify-between px-5 pb-3 pt-2">
        <View>
          <Text className="text-xl font-semibold text-text">Graph</Text>
          <Text className="mt-0.5 text-xs text-textMuted">
            {active.preset.name} · model v2
          </Text>
        </View>
        <CompoundSwitcher />
      </View>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: 8,
          paddingBottom: 48,
        }}
      >
        <View className="mb-5 flex-row gap-2">
          {(Object.keys(WINDOWS) as (keyof typeof WINDOWS)[]).map((key) => (
            <Pressable
              key={key}
              onPress={() => setWindowKey(key)}
              className={cn(
                "rounded-full px-4 py-2",
                key === windowKey ? "bg-text" : "bg-surfaceHi",
              )}
            >
              <Text
                className={cn(
                  "text-xs font-medium",
                  key === windowKey ? "text-white" : "text-textDim",
                )}
              >
                {key}
              </Text>
            </Pressable>
          ))}
        </View>
        <View className="mb-4">
          <ProjectionTimingPicker />
        </View>
        {chart.error != null ? (
          <View role="alert" className="rounded-xl bg-warning/10 p-4">
            <Text className="text-sm text-warning">
              Projection unavailable: {chart.error}. Check your dose log and
              schedule in Profile.
            </Text>
          </View>
        ) : (
          <>
            <Text className="text-lg font-semibold text-text">
              {chart.baseFraction !== null
                ? "Modeled active testosterone ester"
                : "Modeled active amount"}
            </Text>
            <Text className="mt-1 text-xs leading-5 text-textDim">
              {chart.baseFraction !== null
                ? "Milligrams of the injected ester in the model’s central compartment. Serum concentration is shown separately where a supported model is available."
                : "Estimated central amount from the absorption and elimination model."}
            </Text>
            {timing === "now" && chart.model.scenarioApplied !== "now" && (
              <Text className="mt-3 text-xs leading-5 text-textDim">
                {chart.model.takeNowUnavailableReason ??
                  "Resolve pending entries before calculating a new dose."}{" "}
                The available future curve uses the scheduled scenario.
              </Text>
            )}
            {chart.model.calculationError && (
              <View role="alert" className="mt-4 rounded-xl bg-warning/10 p-3">
                <Text className="text-sm text-warning">
                  Future scenario unavailable: {chart.model.calculationError}.
                  The chart shows confirmed-dose decay.
                </Text>
              </View>
            )}
            {chart.model.unresolvedDoses.length > 0 && (
              <View role="alert" className="mt-4 rounded-xl bg-warning/10 p-3">
                <Text className="text-sm text-warning">
                  Resolve {chart.model.unresolvedDoses.length} past pending
                  dose(s) in Home or Log. The future curve shows confirmed-dose
                  decay only; no new injections are assumed.
                </Text>
              </View>
            )}
            <View className="my-5 flex-row flex-wrap gap-6">
              <Stat label="Now" value={formatMg(chart.current)} />
              <Stat label="Window peak" value={formatMg(chart.peak)} />
              <Stat label="Long-run mean" value={formatMg(chart.mean)} />
              <Stat
                label="vs mean"
                value={percentDifference(chart.current, chart.mean)}
              />
            </View>
            <ProjectionPlot
              samples={chart.samples}
              from={chart.from}
              to={chart.to}
              now={now}
              mean={chart.mean}
              formatValue={formatMg}
              color="#F97316"
              label="Modeled active amount with actual history, future scenario, and reference schedule"
              decayOnly={Boolean(
                chart.model.unresolvedDoses.length ||
                chart.model.calculationError,
              )}
            />
            <Text className="mt-2 text-xs leading-5 text-textDim">
              Orange: confirmed history; dashed orange: future scenario. Slate:
              the reference following your schedule history. Dotted horizontal
              line: the current schedule’s long-run mean, not a flat target for
              each moment.
            </Text>
            <Text className="mt-4 text-xs leading-5 text-textMuted">
              Future estimates assume saved plans and each displayed model dose
              happen as shown. The model matches the next reference point; it
              does not guarantee the same peak or total exposure. Absorption and
              clearance vary between people. Dose values refer to the injected
              compound, while this chart{" "}
              {chart.baseFraction !== null
                ? "shows modeled active ester mass"
                : "shows modeled active mass"}
              .
            </Text>
            {isTestosteronePreset(active.preset) && (
              <View className="mt-6 rounded-2xl border border-border bg-surface p-4">
                <Text className="text-lg font-semibold text-text">
                  Estimated serum testosterone
                </Text>
                {active.preset.id === "test-e" && (
                  <View
                    className="my-3 flex-row gap-2"
                    aria-label="Serum model injection route"
                  >
                    {(["im", "sc"] as const).map((choice) => (
                      <Pressable
                        key={choice}
                        aria-pressed={route === choice}
                        onPress={() => setRoute(choice)}
                        className={cn(
                          "rounded-full px-3 py-2",
                          route === choice ? "bg-text" : "bg-surfaceHi",
                        )}
                      >
                        <Text
                          className={cn(
                            "text-xs",
                            route === choice ? "text-white" : "text-textDim",
                          )}
                        >
                          {choice === "im" ? "Intramuscular" : "Subcutaneous"}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                )}
                {chart.serum ? (
                  <>
                    <Text className="mt-1 text-xs leading-5 text-textDim">
                      {chart.serum.model.label}.{" "}
                      {chart.serum.model.evidence === "legacy"
                        ? "Illustrative only; not a validated blood-test prediction."
                        : "Population estimate, not a measured lab result."}
                    </Text>
                    <View className="my-4 flex-row flex-wrap gap-5">
                      <Stat
                        label="Serum T now"
                        value={formatNgDl(chart.serum.current)}
                      />
                      <Stat
                        label="Window peak"
                        value={formatNgDl(chart.serum.peak)}
                      />
                      <Stat
                        label="Long-run mean"
                        value={formatNgDl(chart.serum.mean)}
                      />
                      <Stat
                        label="vs mean"
                        value={percentDifference(
                          chart.serum.current,
                          chart.serum.mean,
                        )}
                      />
                    </View>
                    <ProjectionPlot
                      samples={chart.serum.samples}
                      from={chart.from}
                      to={chart.to}
                      now={now}
                      mean={chart.serum.mean}
                      formatValue={formatNgDl}
                      color="#2563EB"
                      label="Estimated serum testosterone in ng/dL with actual history, future scenario, and reference schedule"
                      decayOnly={Boolean(
                        chart.model.unresolvedDoses.length ||
                        chart.model.calculationError,
                      )}
                    />
                    <Text className="mt-2 text-xs leading-5 text-textDim">
                      Blue: confirmed history; dashed blue: future scenario.
                      Slate: reference schedule. Dotted horizontal line:
                      long-run mean.
                    </Text>
                    <Text className="mt-3 text-xs leading-5 text-textDim">
                      {chart.serum.model.assumption}
                    </Text>
                    {chart.serum.model.sourceUrl && (
                      <a
                        href={chart.serum.model.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-xs underline"
                      >
                        Published model and study population
                      </a>
                    )}
                  </>
                ) : (
                  <Text className="mt-2 text-xs leading-5 text-textDim">
                    A supported population serum model is not yet available for
                    this formulation. Your active-amount graph and injection
                    projections remain available.
                  </Text>
                )}
              </View>
            )}
            <View className="mt-6 rounded-2xl border border-border bg-surface p-4">
              <Text className="mb-3 text-sm font-semibold text-text">
                Injections
              </Text>
              <div
                style={{
                  position: "relative",
                  height: 56,
                  background: "#FAFAFA",
                  overflow: "hidden",
                }}
                aria-label="Injection timeline"
              >
                <div
                  style={{
                    position: "absolute",
                    left: `${((+now - +chart.from) / (+chart.to - +chart.from)) * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: 1.5,
                    background: "#18181B",
                  }}
                  title="Now"
                />
                {chart.doseMarkers.map((dose, i) => (
                  <div
                    key={`${dose.t}-${i}`}
                    title={`${formatShortDateTime(new Date(dose.t))} · ${formatMg(dose.amount)} · ${dose.kind}`}
                    style={{
                      position: "absolute",
                      left: `${((dose.t - +chart.from) / (+chart.to - +chart.from)) * 100}%`,
                      bottom: 8,
                      width: 3,
                      height: dose.kind === "confirmed" ? 32 : 24,
                      background: "#F97316",
                      opacity: dose.kind === "confirmed" ? 1 : 0.4,
                    }}
                  />
                ))}
              </div>
              <View className="mt-2 flex-row justify-between">
                <Text className="text-xs text-textMuted">
                  {WINDOWS[windowKey].back}d ago
                </Text>
                <Text className="text-xs text-textMuted">
                  +{WINDOWS[windowKey].forward}d
                </Text>
              </View>
              <Text className="mt-2 text-xs text-textDim">
                Solid: confirmed injections. Faded: saved plans and scenario
                injections. Black line: now. Skips have no injection marker.
              </Text>
            </View>
            {chart.model.upcoming.length > 0 && (
              <View className="mt-6 gap-2">
                <Text className="text-sm font-semibold text-text">
                  Scenario injections
                </Text>
                {chart.model.upcoming
                  .filter((dose) => +dose.takeAt <= +chart.to)
                  .slice(0, 8)
                  .map((dose, i) => (
                    <View
                      key={`${+dose.takeAt}-${i}`}
                      className="border-b border-border py-2"
                    >
                      <Text className="text-sm text-text">
                        {formatShortDateTime(dose.takeAt)} ·{" "}
                        {dose.kind === "skip"
                          ? "No injection"
                          : formatMg(dose.doseMg)}
                        {dose.kind === "planned" ? " · saved plan" : ""}
                      </Text>
                      <Text className="mt-1 text-xs text-textDim">
                        {dose.reason}
                      </Text>
                    </View>
                  ))}
              </View>
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <View>
      <Text className="text-xs text-textMuted">{label}</Text>
      <Text className="mt-1 text-xl font-semibold text-text">{value}</Text>
    </View>
  );
}

function formatNgDl(value: number) {
  return `${Math.round(value)} ng/dL`;
}
function percentDifference(value: number, mean: number) {
  if (mean <= 0) return "—";
  const percent = ((value - mean) / mean) * 100;
  return `${percent >= 0 ? "+" : ""}${percent.toFixed(0)}%`;
}
function ProjectionPlot({
  samples,
  from,
  to,
  now,
  mean,
  formatValue,
  color,
  label,
  decayOnly,
}: {
  samples: ReturnType<typeof sampleProjection>;
  from: Date;
  to: Date;
  now: Date;
  mean: number;
  formatValue: (value: number) => string;
  color: string;
  label: string;
  decayOnly: boolean;
}) {
  return (
    <div style={{ width: "100%", height: 320 }} aria-label={label}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={samples}
          margin={{ top: 12, right: 12, bottom: 8, left: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#E4E4E7"
            vertical={false}
          />
          <XAxis
            dataKey="t"
            type="number"
            domain={[+from, +to]}
            tickFormatter={(v: number) => format(new Date(v), "d MMM")}
            tick={{ fontSize: 10 }}
            minTickGap={35}
          />
          <YAxis
            tickFormatter={(v: number) => Number(v.toPrecision(3)).toString()}
            tick={{ fontSize: 10 }}
            width={44}
            domain={[0, "auto"]}
          />
          <Tooltip
            labelFormatter={(value) =>
              formatShortDateTime(new Date(Number(value)))
            }
            formatter={(value, name) => [formatValue(Number(value)), name]}
            contentStyle={{ borderRadius: 12, fontSize: 12 }}
          />
          <ReferenceLine y={mean} stroke="#A1A1AA" strokeDasharray="2 5" />
          <Area
            dataKey="actual"
            name="Confirmed history"
            type="linear"
            stroke={color}
            fill={color}
            fillOpacity={0.1}
            strokeWidth={2}
            isAnimationActive={false}
          />
          <Area
            dataKey="projected"
            name={decayOnly ? "Confirmed-dose decay" : "Future scenario"}
            type="linear"
            stroke={color}
            fill={color}
            fillOpacity={0.035}
            strokeDasharray="6 4"
            strokeWidth={2}
            isAnimationActive={false}
          />
          <Line
            dataKey="reference"
            name="Reference schedule"
            type="linear"
            stroke="#64748B"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
          <ReferenceLine
            x={+now}
            stroke="#71717A"
            label={{
              value: "Now",
              fontSize: 10,
              position: "insideTopRight",
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
